import { LitElement, PropertyValues } from 'lit';

/**
 * WebAwesome Adapter for UI Library
 * Brings WebAwesome's form validation and features to our components
 */

// Form validation types from WebAwesome
export interface ValidationResult {
  valid: boolean;
  message?: string;
  customError?: boolean;
}

export interface FormControlOptions {
  value?: string | number | boolean;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  name?: string;
  form?: string;
}

/**
 * Mixin to add WebAwesome-style form validation to UI components
 */
export function FormValidation<T extends new (...args: any[]) => LitElement>(Base: T) {
  class FormValidationClass extends Base {
    required = false;
    disabled = false;
    readonly = false;
    name = '';
    form = '';
    validationMessage = '';
    
    private _value: any = '';
    private _validity: ValidityState = {
      badInput: false,
      customError: false,
      patternMismatch: false,
      rangeOverflow: false,
      rangeUnderflow: false,
      stepMismatch: false,
      tooLong: false,
      tooShort: false,
      typeMismatch: false,
      valid: true,
      valueMissing: false
    };

    static get properties() {
      return {
        ...super.properties,
        required: { type: Boolean, reflect: true },
        disabled: { type: Boolean, reflect: true },
        readonly: { type: Boolean, reflect: true },
        name: { type: String },
        form: { type: String },
        validationMessage: { type: String },
        value: { type: String }
      };
    }

    get value() {
      return this._value;
    }

    set value(val: any) {
      const oldVal = this._value;
      this._value = val;
      this.requestUpdate('value', oldVal);
      this.checkValidity();
    }

    get validity(): ValidityState {
      return this._validity;
    }

    get validationError(): boolean {
      return !this._validity.valid;
    }

    /**
     * Check validity of the form control
     */
    checkValidity(): boolean {
      const wasValid = this._validity.valid;
      
      // Reset validity
      this._validity = {
        badInput: false,
        customError: false,
        patternMismatch: false,
        rangeOverflow: false,
        rangeUnderflow: false,
        stepMismatch: false,
        tooLong: false,
        tooShort: false,
        typeMismatch: false,
        valid: true,
        valueMissing: false
      };

      // Check required
      if (this.required && !this.value) {
        this._validity.valueMissing = true;
        this._validity.valid = false;
        this.validationMessage = 'Please fill out this field.';
      }

      // Dispatch events
      if (wasValid !== this._validity.valid) {
        this.dispatchEvent(new Event(this._validity.valid ? 'wa-valid' : 'wa-invalid'));
      }

      return this._validity.valid;
    }

    /**
     * Report validity (show validation message)
     */
    reportValidity(): boolean {
      const isValid = this.checkValidity();
      
      if (!isValid && this.validationMessage) {
        // In a real implementation, this would show a validation tooltip
        console.warn(`Validation error: ${this.validationMessage}`);
        this.dispatchEvent(new CustomEvent('wa-validation-error', {
          detail: { message: this.validationMessage },
          bubbles: true,
          composed: true
        }));
      }

      return isValid;
    }

    /**
     * Set custom validity message
     */
    setCustomValidity(message: string): void {
      this.validationMessage = message;
      this._validity.customError = !!message;
      this._validity.valid = !message;
      
      if (message) {
        this.dispatchEvent(new Event('wa-invalid'));
      }
    }

    /**
     * Clear validation
     */
    clearValidity(): void {
      this.setCustomValidity('');
      this.validationMessage = '';
    }

    connectedCallback() {
      super.connectedCallback();
      
      // Auto-associate with form if inside one
      const form = this.closest('form');
      if (form && !this.form) {
        this.form = form.id || '';
      }
    }

    protected updated(changedProperties: PropertyValues) {
      super.updated(changedProperties);
      
      // Validate on value change
      if (changedProperties.has('value')) {
        this.checkValidity();
      }
    }
  }
  
  return FormValidationClass;
}

/**
 * WebAwesome event emitter pattern
 */
export function emit<T = Record<string, unknown>>(
  el: HTMLElement,
  name: string,
  options?: CustomEventInit<T>
): CustomEvent<T> {
  const event = new CustomEvent<T>(name, {
    bubbles: true,
    cancelable: false,
    composed: true,
    detail: {},
    ...options
  });

  el.dispatchEvent(event);
  return event;
}

/**
 * WebAwesome wait utility
 */
export function waitForEvent(el: HTMLElement, eventName: string): Promise<void> {
  return new Promise(resolve => {
    function done(event: Event) {
      if (event.target === el) {
        el.removeEventListener(eventName, done);
        resolve();
      }
    }
    el.addEventListener(eventName, done);
  });
}

/**
 * WebAwesome animation utility
 */
export interface AnimationOptions {
  keyframes: Keyframe[];
  options?: KeyframeAnimationOptions;
}

export async function animateTo(
  el: HTMLElement,
  keyframes: Keyframe[],
  options?: KeyframeAnimationOptions
): Promise<void> {
  return new Promise(resolve => {
    if (options?.duration === Infinity) {
      throw new Error('Animation duration cannot be Infinity');
    }

    const animation = el.animate(keyframes, {
      ...options,
      duration: prefersReducedMotion() ? 0 : options?.duration
    });

    animation.addEventListener('cancel', resolve, { once: true });
    animation.addEventListener('finish', resolve, { once: true });
  });
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  const query = window.matchMedia('(prefers-reduced-motion: reduce)');
  return query.matches;
}

/**
 * WebAwesome theme management
 */
export const waThemes = {
  brand: {
    '--wa-color-primary-50': '#eff6ff',
    '--wa-color-primary-100': '#dbeafe',
    '--wa-color-primary-200': '#bfdbfe',
    '--wa-color-primary-300': '#93c5fd',
    '--wa-color-primary-400': '#60a5fa',
    '--wa-color-primary-500': '#3b82f6',
    '--wa-color-primary-600': '#2563eb',
    '--wa-color-primary-700': '#1d4ed8',
    '--wa-color-primary-800': '#1e40af',
    '--wa-color-primary-900': '#1e3a8a',
    '--wa-color-primary-950': '#172554'
  },
  success: {
    '--wa-color-success-50': '#f0fdf4',
    '--wa-color-success-100': '#dcfce7',
    '--wa-color-success-200': '#bbf7d0',
    '--wa-color-success-300': '#86efac',
    '--wa-color-success-400': '#4ade80',
    '--wa-color-success-500': '#22c55e',
    '--wa-color-success-600': '#16a34a',
    '--wa-color-success-700': '#15803d',
    '--wa-color-success-800': '#166534',
    '--wa-color-success-900': '#14532d',
    '--wa-color-success-950': '#052e16'
  },
  warning: {
    '--wa-color-warning-50': '#fffbeb',
    '--wa-color-warning-100': '#fef3c7',
    '--wa-color-warning-200': '#fde68a',
    '--wa-color-warning-300': '#fcd34d',
    '--wa-color-warning-400': '#fbbf24',
    '--wa-color-warning-500': '#f59e0b',
    '--wa-color-warning-600': '#d97706',
    '--wa-color-warning-700': '#b45309',
    '--wa-color-warning-800': '#92400e',
    '--wa-color-warning-900': '#78350f',
    '--wa-color-warning-950': '#451a03'
  },
  danger: {
    '--wa-color-danger-50': '#fef2f2',
    '--wa-color-danger-100': '#fee2e2',
    '--wa-color-danger-200': '#fecaca',
    '--wa-color-danger-300': '#fca5a5',
    '--wa-color-danger-400': '#f87171',
    '--wa-color-danger-500': '#ef4444',
    '--wa-color-danger-600': '#dc2626',
    '--wa-color-danger-700': '#b91c1c',
    '--wa-color-danger-800': '#991b1b',
    '--wa-color-danger-900': '#7f1d1d',
    '--wa-color-danger-950': '#450a0a'
  }
};

/**
 * Apply WebAwesome theme to element
 */
export function applyTheme(el: HTMLElement, theme: keyof typeof waThemes): void {
  const themeColors = waThemes[theme];
  Object.entries(themeColors).forEach(([prop, value]) => {
    el.style.setProperty(prop, value);
  });
}

/**
 * WebAwesome accessibility helpers
 */
export const a11y = {
  /**
   * Set ARIA attributes
   */
  setAttributes(el: HTMLElement, attrs: Record<string, string | boolean | number>): void {
    Object.entries(attrs).forEach(([key, value]) => {
      if (value === false || value === null || value === undefined) {
        el.removeAttribute(key);
      } else {
        el.setAttribute(key, String(value));
      }
    });
  },

  /**
   * Announce to screen readers
   */
  announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.style.position = 'absolute';
    announcer.style.left = '-10000px';
    announcer.style.width = '1px';
    announcer.style.height = '1px';
    announcer.style.overflow = 'hidden';
    
    announcer.textContent = message;
    document.body.appendChild(announcer);
    
    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  }
};

/**
 * WebAwesome slot management
 */
export function getTextContent(slot: HTMLSlotElement | null): string {
  if (!slot) return '';
  
  const nodes = slot.assignedNodes({ flatten: true });
  let text = '';

  [...nodes].forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    }
  });

  return text.trim();
}

/**
 * Create WebAwesome-enhanced UI component
 */
export function createWAEnhancedComponent<T extends new (...args: any[]) => LitElement>(
  Base: T,
  options?: {
    formAssociated?: boolean;
    validators?: Array<(value: any) => ValidationResult>;
  }
) {
  class Enhanced extends Base {
    constructor(...args: any[]) {
      super(...args);
      
      // Add WebAwesome event patterns
      this.addEventListener('click', () => emit(this, 'wa-click'));
      this.addEventListener('focus', () => emit(this, 'wa-focus'));
      this.addEventListener('blur', () => emit(this, 'wa-blur'));
    }
  }

  // Apply form validation if requested
  if (options?.formAssociated) {
    return FormValidation(Enhanced);
  }

  return Enhanced;
}